import React, { useRef } from "react";

export default function PrivacyPolicyModal({ open, onClose }) {
  const printRef = useRef();

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const win = window.open("", "", "height=700,width=900");
    win.document.write("<html><head><title>Privacy Policy</title>");
    win.document.write(
      '<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css">'
    );
    win.document.write("</head><body class='p-8 text-gray-800 bg-white'>");
    win.document.write(printContents);
    win.document.write("</body></html>");
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-500">
              Child Care Legal Aid Clinic (CHICALAC)
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="text-sm bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md transition"
            >
              Export / Print
            </button>

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-red-600 text-2xl font-bold transition"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          ref={printRef}
          className="p-6 space-y-6 text-gray-700 leading-relaxed"
        >
          <p>
            This Privacy Policy explains how Child Care Legal Aid Clinic
            (CHICALAC) (“we”, “our”, “us”) collects, uses, and protects your
            personal data in compliance with the{" "}
            <strong>Data Protection Act, 2019 (Kenya)</strong>.
          </p>

          <Section title="1. Information We Collect">
            <ul className="list-disc list-inside space-y-1">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Donation details (amount, method, reference)</li>
              <li>Images submitted or used with consent</li>
            </ul>
          </Section>

          <Section title="2. Lawful Purpose of Collection">
            <ul className="list-disc list-inside space-y-1">
              <li>Providing legal aid services</li>
              <li>Processing and acknowledging donations</li>
              <li>Communicating with clients and supporters</li>
              <li>Compliance with Kenyan legal obligations</li>
            </ul>
          </Section>

          <Section title="3. Image Usage">
            <p>
              Images are only used with explicit consent, particularly where
              they are intended for public, educational, or promotional use.
            </p>
          </Section>

          <Section title="4. Donations & Financial Data">
            <p>
              We do not store sensitive financial or card information. Only
              reference details (such as M-Pesa transaction codes) are retained
              for verification and record-keeping.
            </p>
          </Section>

          <Section title="5. Your Data Protection Rights">
            <ul className="list-disc list-inside space-y-1">
              <li>Right to access and correct your data</li>
              <li>Right to deletion or restriction of processing</li>
              <li>Right to data portability</li>
            </ul>
            <p className="mt-2">
              To exercise your rights, email{" "}
              <strong>childcarelegalaid@gmail.com</strong>.
            </p>
          </Section>

          <Section title="6. Data Sharing">
            <ul className="list-disc list-inside space-y-1">
              <li>We do not sell or trade personal data</li>
              <li>Data is shared only with lawful processors where required</li>
              <li>Disclosure may occur upon lawful request by the ODPC</li>
            </ul>
          </Section>

          <Section title="7. Data Security">
            <p>
              We apply appropriate technical and organizational measures,
              including encryption, controlled access, and regular security
              reviews to protect your data.
            </p>
          </Section>

          <Section title="8. Complaints & Oversight">
            <p>
              Complaints may be lodged with the{" "}
              <a
                href="https://www.odpc.go.ke"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                Office of the Data Protection Commissioner (ODPC)
              </a>
              .
            </p>
          </Section>

          <div className="pt-4 border-t text-sm text-gray-500">
            Effective Date: <span className="font-medium">July 1, 2025</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
    <h2 className="text-lg font-semibold text-gray-900 mb-2">
      {title}
    </h2>
    {children}
  </div>
);
